import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import GridSection from 'components/_layouts/GridSection';
import QuickSearch from 'components/_atoms/QuickSearch';
import PhxBadge from '_lex/_atoms/PhxBadge';
import PageToolbar from '_lex/_atoms/PageToolbar';
import useUrlParams from 'hooks/useUrlParams';
import datetimeHelper from 'helpers/datetimeHelper';
import { useEisFlaggedPersonnel } from '../../_data_sources/useEisFlaggedPersonnel';
import type { EisFlaggedPersonnelItem, EisFlagStatus } from '../../_types';

export type FlaggedPersonnelParams = {
  search?: string | null;
  status?: EisFlagStatus | null;
  sort?: string | null;
};

const defaultParams: FlaggedPersonnelParams = {
  search: null,
  status: null,
  sort: null,
};

const FLAGGED_PERSONNEL_STATUS_OPTIONS: { key: EisFlagStatus | ''; label: string }[] = [
  { key: '', label: 'performanceReporting.eis.details.flaggedPersonnel.filters.allStatuses' },
  { key: 'Active', label: 'performanceReporting.eis.details.flaggedPersonnel.status.active' },
  { key: 'Cleared', label: 'performanceReporting.eis.details.flaggedPersonnel.status.cleared' },
  { key: 'Closed', label: 'performanceReporting.eis.details.flaggedPersonnel.status.closed' },
];

function getStatusBadgeColor(status: EisFlagStatus): 'success' | 'warning' | 'default' {
  if (status === 'Active') return 'success';
  if (status === 'Cleared') return 'warning';
  return 'default';
}

function getStatusLabelKey(status: EisFlagStatus): string {
  const key = status.toLowerCase();
  return `performanceReporting.eis.details.flaggedPersonnel.status.${key}`;
}

export default function FlaggedPersonnel() {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ? Number(id) : 0;
  const [params, setParams] = useUrlParams(defaultParams);
  const [sortModel, setSortModel] = useState<{ field: string; sort: 'asc' | 'desc' }[]>(() => {
    const s = params.sort;
    if (!s) return [];
    return s.split(',').map((entry: string) => {
      const isDesc = entry.startsWith('-');
      return { field: isDesc ? entry.slice(1) : entry, sort: isDesc ? 'desc' : ('asc' as const) };
    });
  });

  const updateParams = useCallback(
    (updated: Partial<FlaggedPersonnelParams>) => {
      setParams((prev: FlaggedPersonnelParams) => {
        const next = { ...prev, ...updated };
        document.body.dispatchEvent(new Event('params-changed'));
        return next;
      });
    },
    [setParams],
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    setSortModel([]);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const isFiltered = !!(params.search || params.status || params.sort);

  const onSearchChange = useCallback((val: string) => updateParams({ search: val?.trim() || null }), [updateParams]);

  const handleSortModelChange = useCallback(
    (model: { field: string; sort: 'asc' | 'desc' }[]) => {
      setSortModel(model);
      if (model.length > 0) {
        const sort = model.map(({ field, sort }) => (sort === 'desc' ? `-${field}` : field)).join(',');
        updateParams({ sort });
      } else {
        updateParams({ sort: null });
      }
    },
    [updateParams],
  );

  const queryParams = useMemo(
    () => ({
      search: params.search || undefined,
      status: params.status || undefined,
      sort: params.sort || undefined,
    }),
    [params.search, params.status, params.sort],
  );

  const flaggedPersonnelQuery = useEisFlaggedPersonnel(ruleId, queryParams, {}, (err) => {
    console.error('Error fetching EIS flagged personnel:', err);
  });

  const allItems = flaggedPersonnelQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const totalCount = flaggedPersonnelQuery.data?.pages[0]?.count ?? 0;

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.name'),
        flex: 2,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.name ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>{params.row?.name ?? '--'}</Typography>
        ),
      },
      flaggedDate: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.flaggedDate'),
        flex: 1.5,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.flaggedDate ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>
            {datetimeHelper.formatDate(params.row?.flaggedDate, { missingDisplay: '--' })}
          </Typography>
        ),
      },
      groups: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.groups'),
        flex: 1.5,
        sortable: false,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.groups ?? '--',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>{params.row?.groups ?? '--'}</Typography>
        ),
      },
      totalRiskScore: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.totalRiskScore'),
        flex: 1.5,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.totalRiskScore ?? 0,
        renderer: (params: { row: EisFlaggedPersonnelItem }) => {
          const row = params.row;
          const score = row?.totalRiskScore ?? 0;
          const over = row?.overThresholdBy ?? 0;
          return (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
            >
              <Typography variant='body1'>{score}</Typography>
              {over > 0 && (
                <PhxBadge
                  color='default'
                  size='sm'
                >
                  {t('performanceReporting.eis.details.flaggedPersonnel.overThreshold', '', { count: String(over) })}
                </PhxBadge>
              )}
            </Stack>
          );
        },
      },
      status: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.status'),
        flex: 1,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.status ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => {
          const status = params.row?.status;
          if (!status) return <Typography variant='body1'>--</Typography>;
          const color = getStatusBadgeColor(status);
          const label = t(getStatusLabelKey(status));
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {label}
            </PhxBadge>
          );
        },
      },
    }),
    [t],
  );

  const isLoading = flaggedPersonnelQuery.isLoading;
  const isError = flaggedPersonnelQuery.isError;

  return (
    <Stack
      spacing={4}
      sx={{ minHeight: 0 }}
    >
      <PageToolbar
        leftItems={
          <Stack
            direction='row'
            spacing={3}
            alignItems='center'
          >
            <QuickSearch
              id='flagged_personnel_search'
              initialValue={params.search || ''}
              onChange={onSearchChange}
              placeholder={t('performanceReporting.eis.details.flaggedPersonnel.search.placeholder')}
            />
            <Select
              id='status'
              value={params.status || ''}
              size='small'
              displayEmpty
              onChange={(e) => updateParams({ status: (e.target.value as EisFlagStatus) || null })}
            >
              {FLAGGED_PERSONNEL_STATUS_OPTIONS.map((x) => (
                <MenuItem
                  key={`status_${x.key || 'all'}`}
                  value={x.key}
                >{`${t('common.status')}: ${t(x.label)}`}</MenuItem>
              ))}
            </Select>
          </Stack>
        }
        rightItems={
          isFiltered ? (
            <Button
              color='neutral'
              size='small'
              onClick={clearParams}
            >
              {t('performanceReporting.eis.details.flaggedPersonnel.actions.clearFilters')}
            </Button>
          ) : null
        }
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
          {t('performanceReporting.eis.details.flaggedPersonnel.errorLoading')}
        </Box>
      ) : allItems.length === 0 ? (
        <Stack
          spacing={3}
          alignItems='center'
          sx={{ p: 4 }}
        >
          <Box
            component='img'
            src='/admin/img/performance-reporting/empty-eis.png'
            alt={t('performanceReporting.eis.details.flaggedPersonnel.emptyState.title')}
          />
          <Typography
            variant='body1'
            sx={{ fontWeight: 600 }}
          >
            {t('performanceReporting.eis.details.flaggedPersonnel.emptyState.title')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('performanceReporting.eis.details.flaggedPersonnel.emptyState.subtitle')}
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ minHeight: 400 }}>
          <GridSection
            columns={columns}
            data={allItems}
            query={flaggedPersonnelQuery}
            totalCount={totalCount}
            sorting={sortModel}
            onSortModelChange={handleSortModelChange}
            gridKey={`flagged-personnel-${ruleId}-${params.search ?? ''}-${params.status ?? ''}-${params.sort ?? ''}`}
            gridProps={{
              getRowId: (row: EisFlaggedPersonnelItem) => row.id,
              disableMultipleColumnsSorting: false,
              sortingOrder: ['asc', 'desc', null],
              sortingMode: 'server',
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
